<template>
  <div class="line-config">
    <div class="line-panel">
      <div class="panel-header">
        <span class="panel-title">线路管理</span>
        <NButton v-if="debugMode" type="primary" size="large" @click="openAddModal()" class="add-btn">
          <template #icon>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
          </template>
          新增
        </NButton>
      </div>
      <div class="line-list">
        <!-- 当前使用 -->
        <div v-if="currentLine" class="line-item is-current">
          <div class="current-info">
            <div class="current-name-row">
              <span class="current-badge">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                当前使用
              </span>
              <span class="current-name">{{ currentLine.name }}</span>
            </div>
            <span class="current-host" v-if="debugMode">{{ currentLine.host }}</span>
            <span class="current-desc">当前 App 请求将通过此线路转发</span>
          </div>
          <div class="current-actions" v-if="debugMode">
            <NButton size="medium" type="warning" ghost @click="openEditModal(currentLine)" class="action-btn">修改</NButton>
          </div>
          <div class="current-mask" />
        </div>
        <!-- 其他线路 -->
        <div
          class="line-item"
          v-for="item in otherLines"
          :key="item.id"
        >
          <div class="line-body">
            <span class="line-name">{{ item.name }}</span>
            <span class="line-host" v-if="debugMode">{{ item.host }}</span>
          </div>
          <div class="line-actions">
            <NButton size="medium" type="info" ghost @click="handleSelectLine(item)" class="action-btn">
              选用
            </NButton>
            <NButton v-if="debugMode" size="medium" type="warning" ghost @click="openEditModal(item)" class="action-btn">
              编辑
            </NButton>
            <NPopconfirm v-if="debugMode" @positive-click="handleDeleteLine(item.id!)">
              <template #trigger>
                <NButton size="medium" type="error" ghost class="action-btn">删除</NButton>
              </template>
              确定删除线路 "{{ item.name }}" 吗？
            </NPopconfirm>
          </div>
        </div>
        <div v-if="lineList.length === 0" class="empty-tip">暂无线路配置，点击「新增」开始添加</div>
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <NModal v-model:show="showModal" preset="dialog" :title="modalTitle" positive-text="确认" negative-text="取消"
      :loading="submitting" @positive-click="handleSubmit" @negative-click="showModal = false; submitting = false">
      <div class="modal-form">
        <div class="form-item">
          <span class="form-label">线路名称</span>
          <NInput v-model:value="form.name" placeholder="如：加速、直连、本地" size="large" />
        </div>
        <div class="form-item">
          <span class="form-label">线路域名</span>
          <NInput v-model:value="form.host" placeholder="如：api.example.com" size="large" />
        </div>
        <div class="form-item" v-if="!editingId">
          <span class="form-label">设为当前线路</span>
          <NSwitch v-model:value="form.isCurrent" size="large" />
        </div>
      </div>
    </NModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject, type Ref } from 'vue';
import {
  NButton,
  NInput,
  NSwitch,
  NModal,
  NPopconfirm,
} from 'naive-ui';
import Tools from '@/ts/Tools';

// ========== 线路列表 ==========
interface LineItem {
  id?: number;
  name: string;
  host: string;
  sort_order: number;
  is_current: number;
  create_time?: number;
  update_time?: number;
}

const lineList = ref<LineItem[]>([]);

// 调试模式：Ctrl+Shift+D 统一切换线路地址显示
const debugMode = inject<Ref<boolean>>('debugMode', ref(false));

// 当前使用的线路
const currentLine = computed(() =>
  lineList.value.find((item) => item.is_current == 1) ?? null
);

// 其他线路（排除当前使用）
const otherLines = computed(() =>
  lineList.value.filter((item) => item.is_current != 1)
);

// ========== 弹窗 ==========
const showModal = ref(false);
const editingId = ref<number | null>(null);
const submitting = ref(false);

const modalTitle = ref('新增线路');
const form = ref({
  name: '',
  host: '',
  sortOrder: 0,
  isCurrent: false,
});

// ========== 加载线路列表 ==========
async function loadLineList() {
  Tools.FetchGetAsync<{ total: number; list: LineItem[] }>(
    'line/list', {}
  ).then((resp) => {
    if (resp.CheckIsOk() && resp.Data) {
      lineList.value = resp.Data.list ?? [];
    }
  });
}

// ========== 打开新增弹窗 ==========
function openAddModal() {
  editingId.value = null;
  modalTitle.value = '新增线路';
  form.value = { name: '', host: '', sortOrder: 0, isCurrent: false };
  showModal.value = true;
}

// ========== 打开编辑弹窗 ==========
function openEditModal(item: LineItem) {
  editingId.value = item.id!;
  modalTitle.value = '编辑线路';
  form.value = {
    name: item.name,
    host: item.host,
    sortOrder: item.sort_order,
    isCurrent: false, // 编辑不传此字段，占位用
  };
  showModal.value = true;
}

// ========== 提交表单 ==========
async function handleSubmit() {
  // 防重复提交
  if (submitting.value) return false;
  submitting.value = true;

  try {
    // 去除首尾空格
    form.value.name = form.value.name.trim();
    form.value.host = form.value.host.trim();

    if (!form.value.name) {
      Tools.AlertError('线路名称不能为空');
      return false;
    }
    if (/\s/.test(form.value.name)) {
      Tools.AlertError('线路名称不能包含空格');
      return false;
    }
    if (!form.value.host) {
      Tools.AlertError('线路域名不能为空');
      return false;
    }
    if (/\s/.test(form.value.host)) {
      Tools.AlertError('线路域名不能包含空格');
      return false;
    }

    if (editingId.value) {
      // 编辑
      const resp = await Tools.FetchAsync<{ id: number }>(
        'line/update',
        {
          id: editingId.value,
          name: form.value.name,
          host: form.value.host,
          sortOrder: form.value.sortOrder,
        },
        'POST'
      );
      if (resp.CheckIsOk()) {
        Tools.AlertSuccess('更新成功');
        showModal.value = false;
        refreshAll();
      }
    } else {
      // 新增
      const resp = await Tools.FetchAsync<{ name: string; host: string }>(
        'line/create',
        {
          name: form.value.name,
          host: form.value.host,
          sortOrder: form.value.sortOrder,
          isCurrent: form.value.isCurrent ? 1 : 0,
        },
        'POST'
      );
      if (resp.CheckIsOk()) {
        Tools.AlertSuccess('新增成功');
        showModal.value = false;
        refreshAll();
      }
    }
  } finally {
    submitting.value = false;
  }
}

// ========== 删除线路 ==========
async function handleDeleteLine(id: number) {
  Tools.FetchGetAsync<{ id: number; wasCurrent: boolean }>(
    'line/delete', { id }
  ).then((resp) => {
    if (resp.CheckIsOk()) {
      Tools.AlertSuccess('删除成功');
      refreshAll();
    }
  });
}

// ========== 选择当前线路 ==========
async function handleSelectLine(item: LineItem) {
  Tools.FetchAsync<{ id: number; name: string; host: string }>(
    'line/select',
    { id: item.id! },
    'POST'
  ).then((resp) => {
    if (resp.CheckIsOk()) {
      Tools.AlertSuccess(`已切换到线路: ${item.name}`);
      refreshAll();
    }
  });
}

// ========== 刷新 ==========
function refreshAll() {
  loadLineList();
}

onMounted(() => {
  refreshAll();
});
</script>

<style scoped lang="scss">
.line-config {
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

// ========== 面板 ==========
.line-panel {
  position: relative;
  // 顶部一道金线（背景层实现，随卡片固定不随内容滚动）
  background-image:
    linear-gradient(90deg, transparent, rgba(246,226,178,.55) 22%, rgba(255,246,224,.85) 50%, rgba(246,226,178,.55) 78%, transparent),
    var(--kp-card-bg);
  background-size: 100% 1px, auto;
  background-repeat: no-repeat, no-repeat;
  background-position: top left, center;
  border: 1px solid var(--kp-gold-line-strong);
  border-radius: var(--kp-radius);
  box-shadow: var(--kp-card-shadow);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .panel-title {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 2px;
    background-image: linear-gradient(180deg, #fdf3da, #d9b773 60%, #b3893f);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    filter: drop-shadow(0 1px 0 rgba(0, 0, 0, .55));
  }

  .add-btn {
    border-radius: 10px;
    height: 38px;
    padding: 0 18px;
    font-size: 14px;
    font-weight: 600;
  }
}

// ========== 线路列表 ==========
.line-list {
  display: flex;
  flex-direction: column;
  gap: 10px;

  .line-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 18px;
    background: linear-gradient(160deg, rgba(255, 240, 210, 0.05), rgba(255, 240, 210, 0.015));
    border-radius: 12px;
    border: 1px solid rgba(216, 178, 95, 0.14);
    box-shadow: inset 0 1px 0 rgba(255, 235, 198, 0.05);
    overflow: hidden;
    transition: border-color .18s, background .18s, box-shadow .18s;

    &:active {
      background: rgba(216, 178, 95, 0.10);
    }

    &:hover {
      border-color: rgba(216, 178, 95, 0.28);
      box-shadow:
        inset 0 1px 0 rgba(255, 235, 198, 0.07),
        0 0 24px -14px rgba(216, 178, 95, 0.55);
    }

    // ======= 当前使用 =======
    &.is-current {
      display: flex;
      align-items: flex-start;
      gap: 14px;
      padding: 18px;
      background-image:
        radial-gradient(80% 90% at 50% -20%, rgba(216, 178, 95, .14), transparent 72%),
        linear-gradient(160deg, rgba(255, 240, 210, 0.05), rgba(255, 240, 210, 0.015));
      border: 1px solid rgba(240, 208, 138, 0.34);
      box-shadow:
        inset 0 1px 0 rgba(255, 235, 198, 0.10),
        0 0 34px -18px rgba(240, 208, 138, 0.55);

      .current-info {
        flex: 1;
        min-width: 0;
        position: relative;
        z-index: 2;
        display: flex;
        flex-direction: column;
        gap: 8px;

        .current-name-row {
          display: flex;
          align-items: center;
          gap: 10px;

          .current-badge {
            flex-shrink: 0;
            display: inline-flex;
            align-items: center;
            gap: 4px;
            font-size: 10.5px;
            font-weight: 600;
            letter-spacing: .8px;
            color: #f8e9c4;
            padding: 3px 10px;
            border-radius: 999px;
            background: linear-gradient(180deg, rgba(216, 178, 95, .26), rgba(168, 124, 57, .12));
            border: 1px solid rgba(240, 208, 138, 0.48);
            box-shadow: 0 0 14px -7px rgba(240, 208, 138, 0.7);
          }

          .current-name {
            font-size: 17px;
            font-weight: 700;
            letter-spacing: 1px;
            color: var(--kp-gold-hi);
            text-shadow: 0 0 18px rgba(216, 178, 95, 0.30);
          }
        }

        .current-host {
          font-size: 13px;
          color: var(--kp-text-2);
          font-family: var(--kp-mono);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          padding: 6px 12px;
          background: rgba(0, 0, 0, 0.28);
          border: 1px solid rgba(216, 178, 95, 0.10);
          border-radius: 8px;
          display: block;
        }

        .current-desc {
          font-size: 12px;
          color: var(--kp-muted);
          line-height: 1.5;
        }
      }

      .current-actions {
        flex-shrink: 0;
        position: relative;
        z-index: 2;

        .action-btn {
          border-radius: 10px;
          height: 36px;
          padding: 0 18px;
          font-size: 14px;
          font-weight: 500;
          border-width: 1.5px;
        }
      }

      .current-mask {
        position: absolute;
        inset: 0;
        background: linear-gradient(180deg, rgba(255, 240, 210, 0.02), transparent 60%);
        border-radius: 12px;
        z-index: 1;
        pointer-events: none;
      }
    }

    // ======= 线路信息 =======
    .line-body {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 5px;

      .line-name {
        font-size: 15px;
        font-weight: 600;
        letter-spacing: .6px;
        color: var(--kp-text);
      }

      .line-host {
        font-size: 12.5px;
        color: var(--kp-muted);
        font-family: var(--kp-mono);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    // ======= 操作按钮 =======
    .line-actions {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-shrink: 0;

      .action-btn {
        border-radius: 10px;
        height: 36px;
        padding: 0 14px;
        font-size: 13.5px;
        font-weight: 500;
        border-width: 1.5px;
      }
    }
  }

  .empty-tip {
    text-align: center;
    padding: 40px 16px;
    color: var(--kp-faint);
    font-size: 13px;
    letter-spacing: 1px;
    background: rgba(255, 240, 210, 0.015);
    border-radius: 12px;
    border: 1px dashed rgba(216, 178, 95, 0.16);
  }
}

// ========== 弹窗表单 ==========
.modal-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 8px 0;

  .form-item {
    display: flex;
    flex-direction: column;
    gap: 6px;

    .form-label {
      font-size: 12px;
      color: var(--kp-muted);
      font-weight: 600;
      letter-spacing: .8px;
    }
  }
}
</style>
